<script setup lang="ts">

import {computed, ref} from "vue";
import {useLoginInfoStore} from "@/stores/loginInfo";
import Translator from "@/utils/common/Translator";
import NotificationPopupItem from "@/components/navBar/notifications/NotificationPopupItem.vue";
import {storeToRefs} from "pinia";
import ListPlace from "@/utils/presenters/ListPlace.ts";
import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import ConstantViewNames from "@/utils/constants/ConstantViewNames.ts";
import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import NoItems from "@/components/navBar/approvals/NoItems.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import NotificationUtils from "@/utils/broadcast/NotificationUtils.ts";
import DTOUserNotification = namaTypes.DTOUserNotification;

let {notifications, notificationsCount} = storeToRefs(useLoginInfoStore());
let isPopupVisible = ref(false);

const viewAllNotificationsLink = computed(() => {
  let place = new ListPlace();
  place.entityType = DomainBaseEntities.ApprovalDefinition;
  place.ascending = false;
  place.listViewName = ConstantViewNames.EMPLOYEE_READ_NOTIFICATIONS_SCREEN_NAME;
  return router.resolve({
    name: RouteNames.LIST_VIEW_ROUTE,
    query: place.fetchQuery()
  }).href;
});

async function markAsRead() {
  await NotificationUtils.markAsReadTop5Notifications();
  isPopupVisible.value = false;
}
</script>

<template>
  <div class="tw-flex tw-justify-center">
    <div :title="Translator.translateId('notifications')" class="tw-relative focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white/75 hover:tw-cursor-pointer">
      <NamaIcon default-icon="nm-notification" class="tw-size-6 compact:tw-size-[22px] ultra:tw-size-5"/>
      <span v-if="notificationsCount>0" v-text="notificationsCount > 99 ? '+99' : notificationsCount"
            class="tw-absolute tw-bg-red-600 tw-rounded-[25%] tw-text-sm ultra:tw-text-xs tw-p-1 ultra:tw-p-0.5 tw-text-namaFontColor-dark tw--top-1/3 tw-left-1/2 tw-leading-[10px]"/>
    </div>
    <q-menu v-if="useLoginInfoStore().isLoggedIn" v-model="isPopupVisible" self="top middle" :offset=[60,15] id="notificationMenu"
            class="tw-max-h-[70vh] tw-w-full desktop:tw-max-w-[40vw] desktop:tw-w-fit desktop:tw-min-w-96 tw-flex tw-flex-col">
      <div
        class="tw-flex tw-shrink-0 tw-justify-between tw-items-center tw-px-2 tw-py-1 tw-border-b-[0.25px] tw-border-gray-300 dark:tw-border-gray-500"
        v-if="notifications&&notifications.length>0">
        <div class="tw-text-base tw-font-bold dark:tw-text-namaFontColor-dark">{{ Translator.translateId("ShowNotifications") }}</div>
        <div class="tw-text-xs tw-text-primary-light dark:tw-text-primary-dark hover:tw-cursor-pointer" @click="markAsRead">
          {{ Translator.translateId("markAsRead") }}
        </div>
      </div>
      <div class="tw-overflow-y-auto tw-grow tw-max-h-full">
          <NotificationPopupItem v-for="(notification, index) in notifications" :key="index" :notification="notification.values as DTOUserNotification"/>
          <NoItems v-if="!notifications||notifications.length==0" :message="Translator.translateId('noNewNotifications')"/>
      </div>
      <div class="tw-w-full flex tw-shrink-0">
        <a
          class="tw-w-full tw-bg-[#4b5563] dark:tw-bg-black tw-rounded-t-none tw-text-base tw-p-2 tw-leading-tight tw-rounded-b-[4px] tw-font-bold tw-text-namaFontColor-dark tw-cursor-pointer tw-text-center"
          :href="viewAllNotificationsLink">
          {{ Translator.translateId('viewAllNotifications') }}
        </a>
      </div>
    </q-menu>
  </div>
</template>

<style>
#notificationMenu {
  @apply dark:tw-bg-namaBackground-deep-dark
}

#notificationMenu .q-scrollarea__content {
  @apply tw-max-w-full
}
</style>
